"use client";

import { useState, useId } from "react";
import type { ReactNode } from "react";

export interface ProductTab {
  /** Уникальный ключ; также используется в id для ARIA. */
  key: string;
  label: string;
  /** Кол-во элементов в табе (опционально), показывается в кружке рядом с label'ом. */
  count?: number;
  content: ReactNode;
}

interface Props {
  tabs: ProductTab[];
}

/**
 * Табы карточки товара: Характеристики / Описание / Комплектация / Доставка.
 * - keyboard nav: ← → / Home / End (WAI-ARIA Tabs).
 * - Скрытые табы рендерятся в DOM с `hidden` атрибутом — это нужно для SEO
 *   (текст характеристик и описания остаётся индексируемым).
 */
export function ProductTabs({ tabs }: Props) {
  const [active, setActive] = useState<string>(tabs[0]?.key ?? "");
  const baseId = useId();

  if (tabs.length === 0) return null;

  const onKeyDown = (e: React.KeyboardEvent, idx: number) => {
    let next = idx;
    if (e.key === "ArrowRight") next = (idx + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    else return;
    e.preventDefault();
    setActive(tabs[next].key);
    // Перенести фокус на новый таб.
    const el = document.getElementById(`${baseId}-tab-${tabs[next].key}`);
    el?.focus();
  };

  return (
    <div className="mt-10">
      <div
        role="tablist"
        aria-label="Информация о товаре"
        className="flex flex-nowrap gap-1 overflow-x-auto border-b border-border [scrollbar-width:thin]"
      >
        {tabs.map((t, idx) => {
          const isActive = t.key === active;
          return (
            <button
              key={t.key}
              id={`${baseId}-tab-${t.key}`}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`${baseId}-panel-${t.key}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActive(t.key)}
              onKeyDown={(e) => onKeyDown(e, idx)}
              className={
                "inline-flex h-12 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 text-sm font-medium transition-colors " +
                (isActive
                  ? "border-brand-500 text-brand-700"
                  : "border-transparent text-text-muted hover:border-border-strong hover:text-text")
              }
            >
              {t.label}
              {typeof t.count === "number" && t.count > 0 && (
                <span
                  aria-hidden
                  className={
                    "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-semibold " +
                    (isActive ? "bg-brand-100 text-brand-700" : "bg-bg-muted text-text-muted")
                  }
                >
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <div
            key={t.key}
            id={`${baseId}-panel-${t.key}`}
            role="tabpanel"
            aria-labelledby={`${baseId}-tab-${t.key}`}
            hidden={!isActive}
            className="pt-6"
          >
            {t.content}
          </div>
        );
      })}
    </div>
  );
}
